/**
 * Context Variables Definitions
 *
 * All available variables for workflow context autocomplete.
 * Variables follow the format {{entity.field}} and are replaced
 * at runtime with actual values.
 */

import type { AvailableContext } from './node-schemas';
import type { ContextVariable, ContextCategory, EnumOption } from '../types/variables';
import type { DateFieldEntityType, DateFieldsMap } from '@troia-v3/types';

// =============================================================================
// ENUM OPTION CATALOGS (pt-BR labels — single source for FilterRow value Selects)
// =============================================================================

export const CONVERSATION_STATUS_OPTIONS: EnumOption[] = [
  { value: 'waiting', label: 'Aguardando' },
  { value: 'active', label: 'Ativa' },
  { value: 'closed', label: 'Fechada' },
];

export const CONVERSATION_PRIORITY_OPTIONS: EnumOption[] = [
  { value: 'low', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export const CONVERSATION_TYPE_OPTIONS: EnumOption[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Grupo' },
];

/**
 * Quem enviou a última mensagem da conversa (`lastMessageSenderType`,
 * desnormalizado no backend — 2026-08-04). `system` fica fora do union por
 * design: mensagens system não atualizam `lastMessage*`. "Atendente ou IA" =
 * operador "é um de" (in) com Atendente + Agente de IA marcados.
 */
export const LAST_MESSAGE_SENDER_OPTIONS: EnumOption[] = [
  { value: 'contact', label: 'Cliente' },
  { value: 'user', label: 'Atendente' },
  { value: 'ai', label: 'Agente de IA' },
  { value: 'automation', label: 'Automação' },
  { value: 'automation-follow', label: 'Follow-up' },
];

export const LEAD_STATUS_OPTIONS: EnumOption[] = [
  { value: 'new', label: 'Novo' },
  { value: 'contacted', label: 'Em contato' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'disqualified', label: 'Desqualificado' },
  { value: 'converted', label: 'Convertido' },
  { value: 'lost', label: 'Perdido' },
];

export const LEAD_PRIORITY_OPTIONS: EnumOption[] = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export const LEAD_TEMPERATURE_OPTIONS: EnumOption[] = [
  { value: 'cold', label: 'Frio' },
  { value: 'warm', label: 'Morno' },
  { value: 'hot', label: 'Quente' },
];

export const LEAD_BUSINESS_STATUS_OPTIONS: EnumOption[] = [
  { value: 'pending', label: 'Pendente' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

export const TICKET_STATUS_OPTIONS: EnumOption[] = [
  { value: 'open', label: 'Aberto' },
  { value: 'pending', label: 'Pendente' },
  { value: 'paused', label: 'Pausado' },
  { value: 'resolved', label: 'Resolvido' },
  { value: 'closed', label: 'Fechado' },
  { value: 'canceled', label: 'Cancelado' },
];

export const TICKET_PRIORITY_OPTIONS: EnumOption[] = LEAD_PRIORITY_OPTIONS;

/**
 * Status do evento de calendário. Cancelado já é excluído por padrão dos
 * gatilhos de data (deleção no Google vira status 'cancelled' — fix
 * 2026-08-07); o filtro existe para quem quiser regras explícitas por status.
 */
export const EVENT_STATUS_OPTIONS: EnumOption[] = [
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'tentative', label: 'Provisório' },
  { value: 'cancelled', label: 'Cancelado' },
];

// =============================================================================
// CONTACT VARIABLES
// =============================================================================

export const CONTACT_VARIABLES: ContextVariable[] = [
  // Basic identification
  { value: 'contact.id', label: 'ID do Contato', fullPath: '{{contact.id}}', type: 'string', category: 'contact', idRef: 'contact' },
  { value: 'contact.name', label: 'Nome', fullPath: '{{contact.name}}', type: 'string', category: 'contact' },
  { value: 'contact.picture', label: 'Foto', fullPath: '{{contact.picture}}', type: 'string', category: 'contact' },

  // Identifiers
  { value: 'contact.identifiers.email[0]', label: 'Email Principal', fullPath: '{{contact.identifiers.email[0]}}', type: 'string', category: 'contact' },
  { value: 'contact.identifiers.phone[0]', label: 'Telefone Principal', fullPath: '{{contact.identifiers.phone[0]}}', type: 'string', category: 'contact' },
  { value: 'contact.identifiers.whatsapp[0]', label: 'WhatsApp', fullPath: '{{contact.identifiers.whatsapp[0]}}', type: 'string', category: 'contact' },
  { value: 'contact.identifiers.instagram[0]', label: 'Instagram', fullPath: '{{contact.identifiers.instagram[0]}}', type: 'string', category: 'contact' },
  { value: 'contact.identifiers.facebook[0]', label: 'Facebook', fullPath: '{{contact.identifiers.facebook[0]}}', type: 'string', category: 'contact' },
  { value: 'contact.identifiers.telegram[0]', label: 'Telegram', fullPath: '{{contact.identifiers.telegram[0]}}', type: 'string', category: 'contact' },

  // Relationships
  { value: 'contact.customerId', label: 'ID do Cliente', fullPath: '{{contact.customerId}}', type: 'string', category: 'contact' },
  { value: 'contact.assigneeId', label: 'ID do Responsável', fullPath: '{{contact.assigneeId}}', type: 'string', category: 'contact', idRef: 'user' },
  { value: 'contact.teamId', label: 'ID do Time', fullPath: '{{contact.teamId}}', type: 'string', category: 'contact', idRef: 'team' },

  // Tags
  { value: 'contact.tags', label: 'Tags (array)', fullPath: '{{contact.tags}}', type: 'array', category: 'contact' },
  { value: 'contact.tags[0]', label: 'Primeira Tag', fullPath: '{{contact.tags[0]}}', type: 'string', category: 'contact' },

  // Dates
  { value: 'contact.createdAt', label: 'Data de Criação', fullPath: '{{contact.createdAt}}', type: 'date', category: 'contact' },
  { value: 'contact.updatedAt', label: 'Data de Atualização', fullPath: '{{contact.updatedAt}}', type: 'date', category: 'contact' },

  // Virtual/Computed Fields
  { value: 'contact.hasFutureEvents', label: 'Possui Agendamento Futuro', fullPath: '{{contact.hasFutureEvents}}', type: 'boolean', category: 'contact', description: 'true se o contato possui eventos de calendário no futuro' },
];

// =============================================================================
// CONVERSATION VARIABLES
// =============================================================================

export const CONVERSATION_VARIABLES: ContextVariable[] = [
  // Core data
  { value: 'conversation.id', label: 'ID da Conversa', fullPath: '{{conversation.id}}', type: 'string', category: 'conversation' },
  { value: 'conversation.subject', label: 'Assunto', fullPath: '{{conversation.subject}}', type: 'string', category: 'conversation' },
  { value: 'conversation.status', label: 'Status', fullPath: '{{conversation.status}}', type: 'string', category: 'conversation', description: 'waiting, active, closed', enumOptions: CONVERSATION_STATUS_OPTIONS },
  { value: 'conversation.priority', label: 'Prioridade', fullPath: '{{conversation.priority}}', type: 'string', category: 'conversation', description: 'low, normal, high, urgent', enumOptions: CONVERSATION_PRIORITY_OPTIONS },
  { value: 'conversation.closeReason', label: 'Motivo de Fechamento', fullPath: '{{conversation.closeReason}}', type: 'string', category: 'conversation' },
  { value: 'conversation.closeNotes', label: 'Notas de Fechamento', fullPath: '{{conversation.closeNotes}}', type: 'string', category: 'conversation' },

  // Channel & type
  { value: 'conversation.channelId', label: 'ID do Canal', fullPath: '{{conversation.channelId}}', type: 'string', category: 'conversation', idRef: 'channel' },
  { value: 'conversation.source', label: 'Origem', fullPath: '{{conversation.source}}', type: 'string', category: 'conversation' },
  { value: 'conversation.conversationType', label: 'Tipo', fullPath: '{{conversation.conversationType}}', type: 'string', category: 'conversation', description: 'individual, group', enumOptions: CONVERSATION_TYPE_OPTIONS },

  // Contact (populated)
  { value: 'conversation.contact.id', label: 'ID do Contato', fullPath: '{{conversation.contact.id}}', type: 'string', category: 'conversation', idRef: 'contact' },
  { value: 'conversation.contact.name', label: 'Nome do Contato', fullPath: '{{conversation.contact.name}}', type: 'string', category: 'conversation' },
  { value: 'conversation.contact.picture', label: 'Foto do Contato', fullPath: '{{conversation.contact.picture}}', type: 'string', category: 'conversation' },
  { value: 'conversation.contact.phone', label: 'Telefone do Contato', fullPath: '{{conversation.contact.phone}}', type: 'string', category: 'conversation' },

  // Group (populated)
  { value: 'conversation.group.id', label: 'ID do Grupo', fullPath: '{{conversation.group.id}}', type: 'string', category: 'conversation' },
  { value: 'conversation.group.name', label: 'Nome do Grupo', fullPath: '{{conversation.group.name}}', type: 'string', category: 'conversation' },

  // Assignee (populated)
  { value: 'conversation.assignee.id', label: 'ID do Responsável', fullPath: '{{conversation.assignee.id}}', type: 'string', category: 'conversation', idRef: 'user' },
  { value: 'conversation.assignee.name', label: 'Nome do Responsável', fullPath: '{{conversation.assignee.name}}', type: 'string', category: 'conversation' },

  // Related entities
  { value: 'conversation.leadId', label: 'ID do Lead', fullPath: '{{conversation.leadId}}', type: 'string', category: 'conversation' },
  { value: 'conversation.ticketId', label: 'ID do Ticket', fullPath: '{{conversation.ticketId}}', type: 'string', category: 'conversation' },
  { value: 'conversation.teamId', label: 'ID do Time', fullPath: '{{conversation.teamId}}', type: 'string', category: 'conversation', idRef: 'team' },

  // Metrics
  { value: 'conversation.messageCount', label: 'Qtd. de Mensagens', fullPath: '{{conversation.messageCount}}', type: 'number', category: 'conversation' },
  { value: 'conversation.lastMessage', label: 'Última Mensagem', fullPath: '{{conversation.lastMessage}}', type: 'string', category: 'conversation' },
  { value: 'conversation.lastMessageAt', label: 'Data da Última Msg', fullPath: '{{conversation.lastMessageAt}}', type: 'date', category: 'conversation' },
  { value: 'conversation.lastMessageSenderType', label: 'Quem enviou a última mensagem', fullPath: '{{conversation.lastMessageSenderType}}', type: 'string', category: 'conversation', description: 'contact, user, ai, automation, automation-follow', enumOptions: LAST_MESSAGE_SENDER_OPTIONS },
  { value: 'conversation.firstResponseTime', label: 'Tempo 1ª Resposta (ms)', fullPath: '{{conversation.firstResponseTime}}', type: 'number', category: 'conversation' },
  { value: 'conversation.averageResponseTime', label: 'Tempo Médio Resposta (ms)', fullPath: '{{conversation.averageResponseTime}}', type: 'number', category: 'conversation' },

  // Tags & metadata
  { value: 'conversation.tags', label: 'Tags (array)', fullPath: '{{conversation.tags}}', type: 'array', category: 'conversation' },
  { value: 'conversation.tags[0]', label: 'Primeira Tag', fullPath: '{{conversation.tags[0]}}', type: 'string', category: 'conversation' },
  { value: 'conversation.category', label: 'Categoria', fullPath: '{{conversation.category}}', type: 'string', category: 'conversation' },

  // Dates
  { value: 'conversation.startedAt', label: 'Data de Início', fullPath: '{{conversation.startedAt}}', type: 'date', category: 'conversation' },
  { value: 'conversation.closedAt', label: 'Data de Fechamento', fullPath: '{{conversation.closedAt}}', type: 'date', category: 'conversation' },
  { value: 'conversation.createdAt', label: 'Data de Criação', fullPath: '{{conversation.createdAt}}', type: 'date', category: 'conversation' },
];

// =============================================================================
// LEAD VARIABLES
// =============================================================================

export const LEAD_VARIABLES: ContextVariable[] = [
  // Base identification
  { value: 'lead.id', label: 'ID do Lead', fullPath: '{{lead.id}}', type: 'string', category: 'lead' },
  { value: 'lead.contactId', label: 'ID do Contato', fullPath: '{{lead.contactId}}', type: 'string', category: 'lead', idRef: 'contact' },

  // Lead data
  { value: 'lead.score', label: 'Score', fullPath: '{{lead.score}}', type: 'number', category: 'lead' },
  { value: 'lead.segment', label: 'Segmento', fullPath: '{{lead.segment}}', type: 'string', category: 'lead' },
  { value: 'lead.description', label: 'Descrição', fullPath: '{{lead.description}}', type: 'string', category: 'lead' },

  // Source & channel
  { value: 'lead.source', label: 'Fonte', fullPath: '{{lead.source}}', type: 'string', category: 'lead', description: 'webhook, conversation, manual, api' },
  { value: 'lead.origin', label: 'Origem', fullPath: '{{lead.origin}}', type: 'string', category: 'lead', description: 'whatsapp, instagram, email, website...' },
  { value: 'lead.channelId', label: 'ID do Canal', fullPath: '{{lead.channelId}}', type: 'string', category: 'lead', idRef: 'channel' },

  // Status & temperature
  { value: 'lead.status', label: 'Status', fullPath: '{{lead.status}}', type: 'string', category: 'lead', description: 'new, contacted, qualified, disqualified, converted, lost', enumOptions: LEAD_STATUS_OPTIONS },
  { value: 'lead.priority', label: 'Prioridade', fullPath: '{{lead.priority}}', type: 'string', category: 'lead', description: 'low, medium, high, urgent', enumOptions: LEAD_PRIORITY_OPTIONS },
  { value: 'lead.temperature', label: 'Temperatura', fullPath: '{{lead.temperature}}', type: 'string', category: 'lead', description: 'cold, warm, hot', enumOptions: LEAD_TEMPERATURE_OPTIONS },
  { value: 'lead.qualifyStatus', label: 'Status de Qualificação', fullPath: '{{lead.qualifyStatus}}', type: 'string', category: 'lead' },

  // Contact (populated)
  { value: 'lead.contact.name', label: 'Nome do Contato', fullPath: '{{lead.contact.name}}', type: 'string', category: 'lead' },
  { value: 'lead.contact.email[0]', label: 'Email do Contato', fullPath: '{{lead.contact.email[0]}}', type: 'string', category: 'lead' },
  { value: 'lead.contact.phone[0]', label: 'Telefone do Contato', fullPath: '{{lead.contact.phone[0]}}', type: 'string', category: 'lead' },

  // Assignee (populated)
  { value: 'lead.assignee.id', label: 'ID do Responsável', fullPath: '{{lead.assignee.id}}', type: 'string', category: 'lead', idRef: 'user' },
  { value: 'lead.assignee.name', label: 'Nome do Responsável', fullPath: '{{lead.assignee.name}}', type: 'string', category: 'lead' },
  { value: 'lead.assignee.email', label: 'Email do Responsável', fullPath: '{{lead.assignee.email}}', type: 'string', category: 'lead' },

  // Funnel (populated)
  { value: 'lead.funnelId', label: 'ID do Funil', fullPath: '{{lead.funnelId}}', type: 'string', category: 'lead', idRef: 'funnel' },
  { value: 'lead.stepId', label: 'Etapa no funil', fullPath: '{{lead.stepId}}', type: 'string', category: 'lead', idRef: 'step' },
  { value: 'lead.funnel.name', label: 'Nome do Funil', fullPath: '{{lead.funnel.name}}', type: 'string', category: 'lead' },

  // Financial
  { value: 'lead.budget', label: 'Orçamento', fullPath: '{{lead.budget}}', type: 'number', category: 'lead' },
  { value: 'lead.wonValue', label: 'Valor Ganho', fullPath: '{{lead.wonValue}}', type: 'number', category: 'lead' },

  // Activity & business
  { value: 'lead.activityStatus', label: 'Status de Atividade', fullPath: '{{lead.activityStatus}}', type: 'string', category: 'lead' },
  { value: 'lead.businessStatus', label: 'Status do Negócio', fullPath: '{{lead.businessStatus}}', type: 'string', category: 'lead', description: 'pending, won, lost', enumOptions: LEAD_BUSINESS_STATUS_OPTIONS },

  // Dates
  { value: 'lead.lastInteractionAt', label: 'Última Interação', fullPath: '{{lead.lastInteractionAt}}', type: 'date', category: 'lead' },
  { value: 'lead.lastFollowUpAt', label: 'Último Follow-up', fullPath: '{{lead.lastFollowUpAt}}', type: 'date', category: 'lead' },
  { value: 'lead.wonDate', label: 'Data de Ganho', fullPath: '{{lead.wonDate}}', type: 'date', category: 'lead' },
  { value: 'lead.lostDate', label: 'Data de Perda', fullPath: '{{lead.lostDate}}', type: 'date', category: 'lead' },
  { value: 'lead.createdAt', label: 'Data de Criação', fullPath: '{{lead.createdAt}}', type: 'date', category: 'lead' },
];

// =============================================================================
// AI PROCESSOR VARIABLES
// =============================================================================

export const AI_VARIABLES: ContextVariable[] = [
  // Core AI response
  { value: 'ai.message', label: 'Resposta da IA', fullPath: '{{ai.message}}', type: 'string', category: 'ai', description: 'Texto de resposta gerado pela IA' },
  { value: 'ai.prompt', label: 'Prompt Usado', fullPath: '{{ai.prompt}}', type: 'string', category: 'ai', description: 'Prompt enviado para a IA' },

  // Token usage
  { value: 'ai.tokens', label: 'Tokens Totais', fullPath: '{{ai.tokens}}', type: 'number', category: 'ai', description: 'Total de tokens utilizados' },
  { value: 'ai.inputTokens', label: 'Tokens de Entrada', fullPath: '{{ai.inputTokens}}', type: 'number', category: 'ai', description: 'Tokens usados no prompt' },
  { value: 'ai.outputTokens', label: 'Tokens de Saída', fullPath: '{{ai.outputTokens}}', type: 'number', category: 'ai', description: 'Tokens usados na resposta' },

  // Model info
  { value: 'ai.model', label: 'Modelo Usado', fullPath: '{{ai.model}}', type: 'string', category: 'ai', description: 'Nome do modelo de IA utilizado' },

  // Tool calls (when AI uses connected tools)
  { value: 'ai.toolCalls', label: 'Chamadas de Tools', fullPath: '{{ai.toolCalls}}', type: 'array', category: 'ai', description: 'Lista de tools chamadas pela IA' },
  { value: 'ai.lastToolResult', label: 'Último Resultado de Tool', fullPath: '{{ai.lastToolResult}}', type: 'string', category: 'ai', description: 'Resultado da última tool executada' },

  // Status
  { value: 'ai.success', label: 'Sucesso', fullPath: '{{ai.success}}', type: 'boolean', category: 'ai', description: 'Se a IA processou com sucesso' },
  { value: 'ai.error', label: 'Erro', fullPath: '{{ai.error}}', type: 'string', category: 'ai', description: 'Mensagem de erro se houver falha' },
];

// =============================================================================
// TICKET VARIABLES
// =============================================================================

export const TICKET_VARIABLES: ContextVariable[] = [
  // Basic identification
  { value: 'ticket.id', label: 'ID do Ticket', fullPath: '{{ticket.id}}', type: 'string', category: 'ticket' },
  { value: 'ticket.ticketNumber', label: 'Número do Ticket', fullPath: '{{ticket.ticketNumber}}', type: 'string', category: 'ticket', description: 'TCK-YYYY-NNNNNN' },
  { value: 'ticket.title', label: 'Título', fullPath: '{{ticket.title}}', type: 'string', category: 'ticket' },
  { value: 'ticket.description', label: 'Descrição', fullPath: '{{ticket.description}}', type: 'string', category: 'ticket' },

  // Status & priority
  { value: 'ticket.status', label: 'Status', fullPath: '{{ticket.status}}', type: 'string', category: 'ticket', description: 'open, pending, paused, resolved, closed, canceled', enumOptions: TICKET_STATUS_OPTIONS },
  { value: 'ticket.priority', label: 'Prioridade', fullPath: '{{ticket.priority}}', type: 'string', category: 'ticket', description: 'low, medium, high, urgent', enumOptions: TICKET_PRIORITY_OPTIONS },
  { value: 'ticket.category', label: 'Categoria', fullPath: '{{ticket.category}}', type: 'string', category: 'ticket' },

  // Tags
  { value: 'ticket.tags', label: 'Tags (array)', fullPath: '{{ticket.tags}}', type: 'array', category: 'ticket' },
  { value: 'ticket.tags[0]', label: 'Primeira Tag', fullPath: '{{ticket.tags[0]}}', type: 'string', category: 'ticket' },

  // Relationships
  { value: 'ticket.customerId', label: 'ID do Cliente', fullPath: '{{ticket.customerId}}', type: 'string', category: 'ticket' },
  { value: 'ticket.contactId', label: 'ID do Contato', fullPath: '{{ticket.contactId}}', type: 'string', category: 'ticket', idRef: 'contact' },
  { value: 'ticket.leadId', label: 'ID do Lead', fullPath: '{{ticket.leadId}}', type: 'string', category: 'ticket' },
  { value: 'ticket.conversationId', label: 'ID da Conversa', fullPath: '{{ticket.conversationId}}', type: 'string', category: 'ticket' },
  { value: 'ticket.channelId', label: 'ID do Canal', fullPath: '{{ticket.channelId}}', type: 'string', category: 'ticket', idRef: 'channel' },

  // Assignment
  { value: 'ticket.assigneeId', label: 'ID do Responsável', fullPath: '{{ticket.assigneeId}}', type: 'string', category: 'ticket', idRef: 'user' },
  { value: 'ticket.teamId', label: 'ID do Time', fullPath: '{{ticket.teamId}}', type: 'string', category: 'ticket', idRef: 'team' },

  // SLA
  { value: 'ticket.slaBreachTime', label: 'Tempo de Breach SLA', fullPath: '{{ticket.slaBreachTime}}', type: 'date', category: 'ticket' },
  { value: 'ticket.slaBreached', label: 'SLA Violado', fullPath: '{{ticket.slaBreached}}', type: 'boolean', category: 'ticket' },
  { value: 'ticket.responseTime', label: 'Tempo de Resposta (ms)', fullPath: '{{ticket.responseTime}}', type: 'number', category: 'ticket' },
  { value: 'ticket.resolutionTime', label: 'Tempo de Resolução (ms)', fullPath: '{{ticket.resolutionTime}}', type: 'number', category: 'ticket' },

  // Resolution
  { value: 'ticket.internalNotes', label: 'Notas Internas', fullPath: '{{ticket.internalNotes}}', type: 'string', category: 'ticket' },
  { value: 'ticket.resolutionSummary', label: 'Resumo da Resolução', fullPath: '{{ticket.resolutionSummary}}', type: 'string', category: 'ticket' },
  { value: 'ticket.customerSatisfaction', label: 'Satisfação (1-5)', fullPath: '{{ticket.customerSatisfaction}}', type: 'number', category: 'ticket' },

  // Source
  { value: 'ticket.source', label: 'Origem', fullPath: '{{ticket.source}}', type: 'string', category: 'ticket' },

  // Dates
  { value: 'ticket.dueDate', label: 'Data de Vencimento', fullPath: '{{ticket.dueDate}}', type: 'date', category: 'ticket' },
  { value: 'ticket.firstResponseAt', label: '1ª Resposta Em', fullPath: '{{ticket.firstResponseAt}}', type: 'date', category: 'ticket' },
  { value: 'ticket.lastResponseAt', label: 'Última Resposta Em', fullPath: '{{ticket.lastResponseAt}}', type: 'date', category: 'ticket' },
  { value: 'ticket.resolvedAt', label: 'Resolvido Em', fullPath: '{{ticket.resolvedAt}}', type: 'date', category: 'ticket' },
  { value: 'ticket.closedAt', label: 'Fechado Em', fullPath: '{{ticket.closedAt}}', type: 'date', category: 'ticket' },
  { value: 'ticket.createdAt', label: 'Criado Em', fullPath: '{{ticket.createdAt}}', type: 'date', category: 'ticket' },
];

// =============================================================================
// CALENDAR EVENT VARIABLES
// =============================================================================

export const EVENT_VARIABLES: ContextVariable[] = [
  { value: 'event.id', label: 'ID do Evento', fullPath: '{{event.id}}', type: 'string', category: 'event' },
  // O CalendarEvent guarda o título em `summary` (convenção Google Calendar) — NÃO
  // existe campo `title`. Usar `event.title` (bug histórico do catálogo) fazia
  // filtros e {{}} nunca casarem. Label pt-BR continua "Título".
  { value: 'event.summary', label: 'Título', fullPath: '{{event.summary}}', type: 'string', category: 'event' },
  { value: 'event.description', label: 'Descrição', fullPath: '{{event.description}}', type: 'string', category: 'event' },
  { value: 'event.eventType', label: 'Tipo do Evento', fullPath: '{{event.eventType}}', type: 'string', category: 'event' },
  { value: 'event.status', label: 'Status do Evento', fullPath: '{{event.status}}', type: 'string', category: 'event', description: 'confirmed, tentative, cancelled', enumOptions: EVENT_STATUS_OPTIONS },
  { value: 'event.contactId', label: 'ID do Contato', fullPath: '{{event.contactId}}', type: 'string', category: 'event', idRef: 'contact' },
  // Quem CRIOU o compromisso. O `CalendarService` grava o usuário autenticado
  // neste campo (o controller o injeta no payload antes de chamar o service),
  // e é ele que permite "criar lead atribuído ao criador do evento".
  { value: 'event.userId', label: 'Criador do Evento', fullPath: '{{event.userId}}', type: 'string', category: 'event', idRef: 'user' },
  { value: 'event.startTime', label: 'Data de Início', fullPath: '{{event.startTime}}', type: 'date', category: 'event' },
  { value: 'event.endTime', label: 'Data de Término', fullPath: '{{event.endTime}}', type: 'date', category: 'event' },
  { value: 'event.createdAt', label: 'Data de Criação', fullPath: '{{event.createdAt}}', type: 'date', category: 'event' },
  { value: 'event.updatedAt', label: 'Data de Atualização', fullPath: '{{event.updatedAt}}', type: 'date', category: 'event' },
];

// =============================================================================
// SYSTEM VARIABLES (tempo — sempre disponíveis, independem do gatilho)
// =============================================================================

const SIM_NAO: EnumOption[] = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

/**
 * Bloco `system` — injetado pelo motor em TODA execução e recalculado quando o
 * fluxo acorda de uma espera. Não depende do gatilho, então nunca é gateado
 * por `availableContext`.
 *
 * O uso principal é a condição "fora do horário comercial": node "Se" com
 * `{{system.isBusinessHours}}` igual a `Não`. A janela vem do calendário de
 * atendimento da empresa (o mesmo do SLA) — respeita feriado e o intervalo
 * entre as faixas do dia.
 */
export const SYSTEM_VARIABLES: ContextVariable[] = [
  { value: 'system.isBusinessHours', label: 'Está no horário comercial', fullPath: '{{system.isBusinessHours}}', type: 'boolean', category: 'system', description: 'Sim dentro da janela de atendimento da empresa; Não fora dela, no fim de semana, no feriado e no intervalo', enumOptions: SIM_NAO },
  { value: 'system.isHoliday', label: 'É feriado', fullPath: '{{system.isHoliday}}', type: 'boolean', category: 'system', description: 'Feriado nacional ou próprio da empresa', enumOptions: SIM_NAO },
  { value: 'system.holidayName', label: 'Nome do feriado', fullPath: '{{system.holidayName}}', type: 'string', category: 'system' },
  { value: 'system.weekday', label: 'Dia da semana (0=Dom … 6=Sáb)', fullPath: '{{system.weekday}}', type: 'number', category: 'system' },
  { value: 'system.time', label: 'Hora agora (HH:mm)', fullPath: '{{system.time}}', type: 'string', category: 'system' },
  { value: 'system.date', label: 'Data de hoje (AAAA-MM-DD)', fullPath: '{{system.date}}', type: 'string', category: 'system' },
  { value: 'system.timezone', label: 'Fuso horário da empresa', fullPath: '{{system.timezone}}', type: 'string', category: 'system' },
];

/**
 * Bloco `cancelamento` — o desvio que fez a execução sair pela saída
 * "Cancelado" de um Aguardar (spec 2026-09-20 §7.2/§7.7). Só existe nos nós
 * abaixo daquela saída, por isso NÃO é oferecido em todo lugar como `system`:
 * quem decide é `isBelowCancelledOutput` (utils/cancelled-branch.ts).
 *
 * Os campos são os do último desvio gravado na execução
 * (`WorkflowCancelDiversion` em @troia-v3/types).
 */
export const CANCELLATION_VARIABLES: ContextVariable[] = [
  { value: 'cancelamento.description', label: 'Motivo do desvio', fullPath: '{{cancelamento.description}}', type: 'string', category: 'cancellation', description: 'Texto pronto, ex.: regra de cancelamento do nó "Aguardar resposta"' },
  { value: 'cancelamento.eventType', label: 'Evento que desviou', fullPath: '{{cancelamento.eventType}}', type: 'string', category: 'cancellation' },
  { value: 'cancelamento.entityKind', label: 'Tipo da entidade do evento', fullPath: '{{cancelamento.entityKind}}', type: 'string', category: 'cancellation' },
  { value: 'cancelamento.entityId', label: 'ID da entidade do evento', fullPath: '{{cancelamento.entityId}}', type: 'string', category: 'cancellation' },
  { value: 'cancelamento.ruleNodeLabel', label: 'Nó dono da regra', fullPath: '{{cancelamento.ruleNodeLabel}}', type: 'string', category: 'cancellation' },
];

// =============================================================================
// SINGLE SOURCE MAP + DERIVED CATALOGS
// =============================================================================

export const ENTITY_VARIABLES_MAP: Record<ContextCategory, ContextVariable[]> = {
  contact: CONTACT_VARIABLES,
  conversation: CONVERSATION_VARIABLES,
  lead: LEAD_VARIABLES,
  ticket: TICKET_VARIABLES,
  ai: AI_VARIABLES,
  event: EVENT_VARIABLES,
  system: SYSTEM_VARIABLES,
  cancellation: CANCELLATION_VARIABLES,
};

export function getVariablesForEntities(entities: ContextCategory[]): ContextVariable[] {
  return entities.flatMap((entity) => ENTITY_VARIABLES_MAP[entity] ?? []);
}

/**
 * Date field options per entity type for trigger_date_field / trigger_inactivity.
 * Ported from the (dead) NodeConfigurator dateFieldOptionsMap and completed
 * against DateFieldsMap in @troia-v3/types — TypeScript enforces valid fields.
 */
export const DATE_FIELD_OPTIONS: { [K in DateFieldEntityType]: Array<{ value: DateFieldsMap[K]; label: string }> } = {
  contact: [
    { value: 'createdAt', label: 'Data de Criação' },
    { value: 'updatedAt', label: 'Última Atualização' },
    { value: 'lastInteractionAt', label: 'Última Interação' },
    { value: 'lastContactedAt', label: 'Último Contato' },
    { value: 'birthDate', label: 'Data de Aniversário' },
  ],
  lead: [
    { value: 'createdAt', label: 'Data de Criação' },
    { value: 'updatedAt', label: 'Última Atualização' },
    { value: 'assignedAt', label: 'Data de Atribuição' },
    { value: 'wonDate', label: 'Data de Fechamento (Ganho)' },
    { value: 'lostDate', label: 'Data de Fechamento (Perdido)' },
    { value: 'expectedCloseDate', label: 'Previsão de Fechamento' },
    { value: 'lastInteractionAt', label: 'Última Interação' },
    { value: 'lastFollowUpAt', label: 'Último Follow-up' },
    { value: 'lastStepAt', label: 'Última Mudança de Etapa' },
    { value: 'lastActivityAt', label: 'Última Atividade' },
  ],
  ticket: [
    { value: 'createdAt', label: 'Data de Criação' },
    { value: 'updatedAt', label: 'Última Atualização' },
    { value: 'assignedAt', label: 'Data de Atribuição' },
    { value: 'dueDate', label: 'Data de Vencimento' },
    { value: 'firstResponseAt', label: 'Primeira Resposta' },
    { value: 'lastResponseAt', label: 'Última Resposta' },
    { value: 'resolvedAt', label: 'Data de Resolução' },
    { value: 'closedAt', label: 'Data de Fechamento' },
    { value: 'slaBreachTime', label: 'Prazo do SLA' },
  ],
  conversation: [
    { value: 'createdAt', label: 'Data de Criação' },
    { value: 'updatedAt', label: 'Última Atualização' },
    { value: 'startedAt', label: 'Data de Início' },
    { value: 'closedAt', label: 'Data de Fechamento' },
    { value: 'lastMessageAt', label: 'Última Mensagem' },
    { value: 'lastMessageFromCustomer', label: 'Última Mensagem do Cliente' },
    { value: 'assignedAt', label: 'Data de Atribuição' },
    { value: 'slaBreachTime', label: 'Prazo do SLA' },
  ],
  event: [
    { value: 'startTime', label: 'Data de Início' },
    { value: 'endTime', label: 'Data de Término' },
    { value: 'createdAt', label: 'Data de Criação' },
    { value: 'updatedAt', label: 'Última Atualização' },
  ],
};

export function getDateFieldOptions(entityType: string): Array<{ value: string; label: string }> {
  const map = DATE_FIELD_OPTIONS as Record<string, Array<{ value: string; label: string }>>;
  return map[entityType] ?? [];
}

/**
 * Top-level watchable fields of an entity (for trigger_event watchFields).
 * Excludes populated sub-objects (contact.name inside lead) and array indexes.
 */
export function getWatchableFieldOptions(entity: ContextCategory): EnumOption[] {
  const vars = ENTITY_VARIABLES_MAP[entity] ?? [];
  return vars
    .filter((v) => {
      const path = v.value.split('.').slice(1).join('.');
      return path.length > 0 && !path.includes('.') && !path.includes('[');
    })
    .map((v) => ({ value: v.value.split('.').slice(1).join('.'), label: v.label }));
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all available variables based on current context
 */
export function getAvailableVariables(context: AvailableContext): ContextVariable[] {
  const variables: ContextVariable[] = [];

  if (context.contact) {
    variables.push(...CONTACT_VARIABLES);
  }
  if (context.conversation) {
    variables.push(...CONVERSATION_VARIABLES);
  }
  if (context.lead) {
    variables.push(...LEAD_VARIABLES);
  }
  if (context.ticket) {
    variables.push(...TICKET_VARIABLES);
  }
  if (context.ai) {
    variables.push(...AI_VARIABLES);
  }
  if (context.event) {
    variables.push(...EVENT_VARIABLES);
  }

  // Sem gate: o bloco `system` é injetado em toda execução, venha de onde vier
  // o gatilho. É o que permite escrever "se fora do horário comercial" em
  // qualquer fluxo, sem node dedicado.
  variables.push(...SYSTEM_VARIABLES);

  return variables;
}

/**
 * Get all variables (regardless of context)
 */
export function getAllVariables(): ContextVariable[] {
  return [
    ...CONTACT_VARIABLES,
    ...CONVERSATION_VARIABLES,
    ...LEAD_VARIABLES,
    ...TICKET_VARIABLES,
    ...AI_VARIABLES,
    ...EVENT_VARIABLES,
  ];
}

/**
 * Filter variables by search term
 */
export function filterVariables(variables: ContextVariable[], searchTerm: string): ContextVariable[] {
  if (!searchTerm) return variables;

  const lowerSearch = searchTerm.toLowerCase();
  return variables.filter(
    (v) =>
      v.value.toLowerCase().includes(lowerSearch) ||
      v.label.toLowerCase().includes(lowerSearch) ||
      v.fullPath.toLowerCase().includes(lowerSearch)
  );
}

/**
 * Group variables by category
 */
export function groupVariablesByCategory(
  variables: ContextVariable[]
): Record<string, ContextVariable[]> {
  return variables.reduce((acc, variable) => {
    if (!acc[variable.category]) {
      acc[variable.category] = [];
    }
    acc[variable.category].push(variable);
    return acc;
  }, {} as Record<string, ContextVariable[]>);
}

/**
 * Category display info
 */
export const CATEGORY_INFO: Record<string, { label: string; icon: string }> = {
  contact: { label: 'Contato', icon: '👤' },
  conversation: { label: 'Conversa', icon: '💬' },
  lead: { label: 'Lead', icon: '🎯' },
  ticket: { label: 'Ticket', icon: '🎫' },
  ai: { label: 'Processador IA', icon: '🤖' },
  event: { label: 'Evento de Calendário', icon: '📅' },
  system: { label: 'Sistema (data e hora)', icon: '🕐' },
  cancellation: { label: 'Desvio pela saída Cancelado', icon: '🚦' },
};
